import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import { formatCpfCnpj, senhaInicial } from './format';
import {
    User,
    Mail,
    Phone,
    Lock,
    IdCard,
    AlertCircle,
    CheckCircle2,
    Loader2,
    ArrowLeft,
    Save,
    KeyRound
} from 'lucide-react';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';

// Portal do Consumidor — perfil e configurações do próprio usuário
// (rota /portal-consumidor/perfil). O consumidor edita nome, WhatsApp e e-mail de
// contato (profiles.email_pessoal — o e-mail de login é sintético, derivado do CPF,
// e não muda) e altera a própria senha. O CPF é o identificador da conta: somente leitura.

const inputClass =
    'block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-50 disabled:text-gray-500';

const InputField = ({
    label,
    icon: Icon,
    ...props
}: { label: string; icon: React.ElementType } & React.InputHTMLAttributes<HTMLInputElement>) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon size={18} className="text-gray-400" />
            </div>
            <input className={inputClass} {...props} />
        </div>
    </div>
);

const Banner = ({ tipo, mensagem }: { tipo: 'erro' | 'ok'; mensagem: string }) => (
    <div className={`mb-5 px-4 py-3 rounded-lg flex items-start gap-3 text-sm animate-fade-in border ${tipo === 'erro'
        ? 'bg-red-50 border-red-200 text-red-700'
        : 'bg-green-50 border-green-200 text-green-700'
        }`}>
        {tipo === 'erro' ? <AlertCircle size={18} className="mt-0.5 shrink-0" /> : <CheckCircle2 size={18} className="mt-0.5 shrink-0" />}
        <span>{mensagem}</span>
    </div>
);

const PortalConsumidorPerfil = () => {
    const { user, profile, loading, signOut, refreshProfile } = useAuth();

    // Dados pessoais
    const [fullName, setFullName] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [emailContato, setEmailContato] = useState('');
    const [dadosMsg, setDadosMsg] = useState<{ tipo: 'erro' | 'ok'; mensagem: string } | null>(null);
    const [salvandoDados, setSalvandoDados] = useState(false);

    // Alterar senha
    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [senhaMsg, setSenhaMsg] = useState<{ tipo: 'erro' | 'ok'; mensagem: string } | null>(null);
    const [salvandoSenha, setSalvandoSenha] = useState(false);

    useTituloPortal('Meu Perfil');

    // Preenche o formulário quando o profile chega
    useEffect(() => {
        if (profile) {
            setFullName(profile.full_name || '');
            setWhatsapp(profile.whatsapp || '');
            setEmailContato(profile.email_pessoal || '');
        }
    }, [profile]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    const handleSalvarDados = async (e: React.FormEvent) => {
        e.preventDefault();
        setDadosMsg(null);

        if (fullName.trim().length < 3) {
            setDadosMsg({ tipo: 'erro', mensagem: 'Informe seu nome completo.' });
            return;
        }
        if (emailContato && !emailContato.includes('@')) {
            setDadosMsg({ tipo: 'erro', mensagem: 'E-mail de contato inválido.' });
            return;
        }

        setSalvandoDados(true);
        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    full_name: fullName.trim(),
                    whatsapp: whatsapp || null,
                    email_pessoal: emailContato || null,
                })
                .eq('id', user.id);

            if (error) {
                setDadosMsg({ tipo: 'erro', mensagem: 'Erro ao salvar. Tente novamente.' });
                return;
            }
            await refreshProfile();
            setDadosMsg({ tipo: 'ok', mensagem: 'Dados atualizados com sucesso!' });
        } catch {
            setDadosMsg({ tipo: 'erro', mensagem: 'Ocorreu um erro inesperado. Tente novamente.' });
        } finally {
            setSalvandoDados(false);
        }
    };

    const handleAlterarSenha = async (e: React.FormEvent) => {
        e.preventDefault();
        setSenhaMsg(null);

        if (novaSenha.length < 6) {
            setSenhaMsg({ tipo: 'erro', mensagem: 'A nova senha deve ter pelo menos 6 caracteres.' });
            return;
        }
        if (profile?.cpf_cnpj && novaSenha === senhaInicial(profile.cpf_cnpj)) {
            setSenhaMsg({ tipo: 'erro', mensagem: 'A nova senha não pode ser os 6 primeiros dígitos do seu CPF.' });
            return;
        }
        if (novaSenha !== confirmarSenha) {
            setSenhaMsg({ tipo: 'erro', mensagem: 'As senhas não conferem.' });
            return;
        }

        setSalvandoSenha(true);
        try {
            const { error } = await supabase.auth.updateUser({ password: novaSenha });
            if (error) {
                setSenhaMsg({ tipo: 'erro', mensagem: error.message || 'Erro ao alterar a senha.' });
                return;
            }
            if (user) {
                const { error: consumidorError } = await supabase
                    .from('consumidores')
                    .update({ senha_provisoria_pendente: false })
                    .eq('id', user.id);
                if (consumidorError) console.error('Erro ao atualizar senha_provisoria_pendente:', consumidorError);
            }
            setNovaSenha('');
            setConfirmarSenha('');
            setSenhaMsg({ tipo: 'ok', mensagem: 'Senha alterada com sucesso!' });
        } catch {
            setSenhaMsg({ tipo: 'erro', mensagem: 'Ocorreu um erro inesperado. Tente novamente.' });
        } finally {
            setSalvandoSenha(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center gap-3">
                    <Link
                        to="/portal-consumidor/inicio"
                        className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
                        <p className="text-sm text-gray-500">Gerencie seus dados e a sua senha de acesso.</p>
                    </div>
                </div>

                {/* Dados pessoais */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <User size={20} className="text-primary" />
                        </div>
                        <h2 className="font-bold text-gray-900">Dados pessoais</h2>
                    </div>

                    {dadosMsg && <Banner tipo={dadosMsg.tipo} mensagem={dadosMsg.mensagem} />}

                    <form onSubmit={handleSalvarDados} className="space-y-5">
                        <InputField
                            label="CPF do assinante (login)"
                            icon={IdCard}
                            type="text"
                            disabled
                            value={profile?.cpf_cnpj ? formatCpfCnpj(profile.cpf_cnpj) : '—'}
                        />
                        <InputField
                            label="Nome completo"
                            icon={User}
                            type="text"
                            required
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Seu nome completo"
                        />
                        <InputField
                            label="E-mail de contato"
                            icon={Mail}
                            type="email"
                            value={emailContato}
                            onChange={(e) => setEmailContato(e.target.value)}
                            placeholder="seu@email.com"
                        />
                        <div>
                            <InputField
                                label="WhatsApp"
                                icon={Phone}
                                type="tel"
                                value={whatsapp}
                                onChange={(e) => setWhatsapp(e.target.value)}
                                placeholder="(98) 99999-9999"
                            />
                            <p className="mt-1.5 text-xs text-gray-400">
                                Este número recebe os lembretes automáticos de vencimento das suas faturas.
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={salvandoDados}
                            className="w-full sm:w-auto flex justify-center items-center gap-2 py-3 px-6 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
                        >
                            {salvandoDados ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                            Salvar alterações
                        </button>
                    </form>
                </div>

                {/* Alterar senha */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                            <KeyRound size={20} className="text-secondary" />
                        </div>
                        <h2 className="font-bold text-gray-900">Alterar senha</h2>
                    </div>

                    {senhaMsg && <Banner tipo={senhaMsg.tipo} mensagem={senhaMsg.mensagem} />}

                    <form onSubmit={handleAlterarSenha} className="space-y-5">
                        <InputField
                            label="Nova senha"
                            icon={Lock}
                            type="password"
                            required
                            minLength={6}
                            value={novaSenha}
                            onChange={(e) => setNovaSenha(e.target.value)}
                            placeholder="Mínimo 6 caracteres"
                        />
                        <InputField
                            label="Confirmar nova senha"
                            icon={Lock}
                            type="password"
                            required
                            minLength={6}
                            value={confirmarSenha}
                            onChange={(e) => setConfirmarSenha(e.target.value)}
                            placeholder="Repita a nova senha"
                        />
                        <button
                            type="submit"
                            disabled={salvandoSenha}
                            className="w-full sm:w-auto flex justify-center items-center gap-2 py-3 px-6 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
                        >
                            {salvandoSenha ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
                            Alterar senha
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default PortalConsumidorPerfil;
